import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Award, Lock } from "lucide-react";
import { usePlanState } from "@/hooks/usePlanState";
import { UpgradePrompt } from "@/components/LockedOverlay";

// Catalog of every possible badge. Earned badges are matched by id.
// Keep in sync with backend BADGE_RULES (or fetch via API later).
const BADGE_CATALOG = [
  { id: "first_steps", name: "First Steps", desc: "Completed your first stage", icon: "👣" },
  { id: "trail_blazer", name: "Trail Blazer", desc: "Completed 3 expeditions", icon: "🥾" },
  { id: "summit_seeker", name: "Summit Seeker", desc: "Reached your first summit", icon: "🏔️" },
  { id: "compass_master", name: "Compass Master", desc: "10-day streak", icon: "🧭" },
  { id: "trailhead", name: "Trailhead", desc: "Created your first climber", icon: "📍" },
  { id: "quiz_ace", name: "Quiz Ace", desc: "Perfect score on a stage quiz", icon: "🎯" },
  { id: "altitude_100", name: "Cloud Walker", desc: "Earned 100 Altitude Points", icon: "☁️" },
  { id: "altitude_500", name: "Sky Climber", desc: "Earned 500 Altitude Points", icon: "🌤️" },
  { id: "altitude_1000", name: "Mountaineer", desc: "Earned 1000 Altitude Points", icon: "⛰️" },
];

const Badges = () => {
  const { user, loading } = useAuth();
  const [students, setStudents] = useState([]);
  const [busy, setBusy] = useState(true);
  const { state: planState } = usePlanState();

  useEffect(() => {
    if (!user) return;
    api.get("/students").then((r) => {
      setStudents(r.data || []);
    }).finally(() => setBusy(false));
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-mm-navy/60">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />
      <div className="container mx-auto py-10" data-testid="badges-page">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Trail honors</div>
        <h1 className="font-heading text-4xl font-black text-mm-navy mt-2">Scout badges</h1>
        <p className="text-mm-navy/70 mt-2 max-w-2xl">
          Every climber earns badges for milestones along the trail. Locked badges show what's still waiting on the mountain.
        </p>

        {planState && !planState.is_premium && (
          <div className="mt-5" data-testid="badges-upgrade-prompt-wrap">
            <UpgradePrompt
              testid="badges-upgrade-prompt"
              icon={Award}
              message="Unlock the full badge collection with Summit Pass — every premium badge becomes earnable across all your climbers."
            />
          </div>
        )}

        {busy && <div className="text-mm-navy/60 mt-8">Loading badges…</div>}

        {!busy && students.length === 0 && (
          <div className="mt-8 text-mm-navy/60 py-10 text-center border-2 border-dashed border-mm-navy/15 rounded-xl">
            Add a climber first — their badges will show up here.
          </div>
        )}

        {!busy && students.map((s) => {
          const earnedIds = new Set((s.badges || []).map((b) => (typeof b === "string" ? b : b.id)));
          const earnedCount = earnedIds.size;
          return (
            <div key={s.id} className="mt-8 stage-card p-7" data-testid={`badges-climber-${s.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-heading text-2xl font-black text-mm-navy">{s.name}</div>
                  <div className="text-xs text-mm-navy/60 mt-0.5">Grade {s.grade}</div>
                </div>
                <div className="px-3 py-1.5 rounded-full bg-mm-gold/20 text-mm-navy text-sm font-bold">
                  {earnedCount} / {BADGE_CATALOG.length} earned
                </div>
              </div>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {BADGE_CATALOG.map((b) => {
                  const earned = earnedIds.has(b.id);
                  return (
                    <div
                      key={b.id}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        earned
                          ? "bg-mm-gold/10 border-mm-gold/40"
                          : "bg-mm-parchment border-mm-navy/10 opacity-60"
                      }`}
                      data-testid={`badge-${s.id}-${b.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-3xl" aria-hidden="true">{earned ? b.icon : "🔒"}</div>
                        {earned ? (
                          <Award size={16} strokeWidth={2.5} className="text-mm-gold" />
                        ) : (
                          <Lock size={14} strokeWidth={2.5} className="text-mm-navy/30" />
                        )}
                      </div>
                      <div className="mt-2 font-heading font-bold text-mm-navy text-sm">{b.name}</div>
                      <div className="text-xs text-mm-navy/60 mt-0.5">{b.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Badges;
